import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatCurrency(amount: number, currency = 'AUD', locale = 'en-AU') {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(amount);
}

/** Explicit ISO codes disambiguate AUD/NZD once, without duplicating Intl's NZD prefix. */
export function formatCurrencyCode(amount: number, currency = 'AUD', locale = 'en-AU') {
  return new Intl.NumberFormat(locale, { style: 'currency', currency, currencyDisplay: 'code', minimumFractionDigits: 2 }).format(amount);
}

/**
 * Pull the leading currency symbol out of a formatted amount. Used by hero
 * displays (HomeHero, NetSavingsCard) so the symbol can be rendered at a
 * smaller / muted size next to the big serif number — `$ 2,481.30` reads
 * more editorially than the flush `$2,481.30` that Intl gives you. For AUD
 * this returns "A$" (matches Intl's en-AU output); JPY → "¥" etc.
 */
export function currencySymbol(currency: string, locale = 'en-AU'): string {
  const formatted = formatCurrency(0, currency, locale);
  const match = formatted.match(/^[^\d-]+/);
  return match ? match[0].trim() : currency;
}

export function formatDate(input: string | Date, locale = 'en-AU') {
  const date = typeof input === 'string' ? new Date(input) : input;
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date);
}

const EN_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function parseLocalDate(s: string): Date {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function lastDayOfMonth(year: number, monthZeroBased: number): number {
  return new Date(year, monthZeroBased + 1, 0).getDate();
}

export function formatCompactRange(
  from: string,
  to: string,
  locale: string,
  fyStartMonth = 7,
): string {
  const a = parseLocalDate(from);
  const b = parseLocalDate(to);
  const isZh = locale.startsWith('zh');

  const aY = a.getFullYear();
  const aM = a.getMonth();
  const aD = a.getDate();
  const bY = b.getFullYear();
  const bM = b.getMonth();
  const bD = b.getDate();

  const sameDay = aY === bY && aM === bM && aD === bD;
  const aIsMonthStart = aD === 1;
  const bIsMonthEnd = bD === lastDayOfMonth(bY, bM);
  const sameYear = aY === bY;
  const sameMonth = sameYear && aM === bM;

  // 1. Single day
  if (sameDay) {
    return isZh ? `${aY}年${aM + 1}月${aD}日` : `${aD} ${EN_MONTHS[aM]} ${aY}`;
  }

  // 2. Financial year — fyStart/1 → (fyStart-1)/lastDay, year diff exactly 1
  const fyStartZeroBased = fyStartMonth - 1;
  const fyEndZeroBased = (fyStartZeroBased + 11) % 12;
  if (
    fyStartMonth !== 1 &&
    aIsMonthStart &&
    aM === fyStartZeroBased &&
    bIsMonthEnd &&
    bM === fyEndZeroBased &&
    bY - aY === 1
  ) {
    const shortB = String(bY).slice(-2);
    return isZh ? `${aY}/${shortB}财年` : `FY ${aY}–${shortB}`;
  }

  // 3. Full calendar year (Jan 1 → Dec 31, same year)
  if (sameYear && aIsMonthStart && aM === 0 && bIsMonthEnd && bM === 11) {
    return isZh ? `${aY}年` : `${aY}`;
  }

  // 4. Full month (same year, same month, day 1 → last day)
  if (sameMonth && aIsMonthStart && bIsMonthEnd) {
    return isZh ? `${aY}年${aM + 1}月` : `${EN_MONTHS[aM]} ${aY}`;
  }

  // 5. Same month, partial
  if (sameMonth) {
    return isZh
      ? `${aY}年${aM + 1}月${aD}日–${bD}日`
      : `${aD}–${bD} ${EN_MONTHS[aM]} ${aY}`;
  }

  // 6. Cross-month, same year
  if (sameYear) {
    if (aIsMonthStart && bIsMonthEnd) {
      return isZh
        ? `${aY}年${aM + 1}月–${bM + 1}月`
        : `${EN_MONTHS[aM]} – ${EN_MONTHS[bM]} ${aY}`;
    }
    return isZh
      ? `${aY}年${aM + 1}月${aD}日–${bM + 1}月${bD}日`
      : `${aD} ${EN_MONTHS[aM]} – ${bD} ${EN_MONTHS[bM]} ${aY}`;
  }

  // 7. Cross-year
  if (aIsMonthStart && bIsMonthEnd) {
    return isZh
      ? `${aY}年${aM + 1}月–${bY}年${bM + 1}月`
      : `${EN_MONTHS[aM]} ${aY} – ${EN_MONTHS[bM]} ${bY}`;
  }
  return isZh
    ? `${aY}年${aM + 1}月${aD}日–${bY}年${bM + 1}月${bD}日`
    : `${aD} ${EN_MONTHS[aM]} ${aY} – ${bD} ${EN_MONTHS[bM]} ${bY}`;
}
